import React from 'react';
import { Link } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface PageBreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumb navigation for deep pages.
 * On mobile, collapses middle items to "..." and shows only first + last 2.
 *
 * Usage:
 *   <PageBreadcrumb items={[
 *     { label: 'Panel', to: '/dashboard' },
 *     { label: 'Projeler', to: '/dashboard' },
 *     { label: 'Proje adı' }, // last, no link
 *   ]} />
 */
const PageBreadcrumb: React.FC<PageBreadcrumbProps> = ({ items, className = '' }) => {
  if (items.length === 0) return null;

  // Mobile strategy: if > 2 items, show first + ellipsis + last. Desktop: show all.
  const shouldCollapseOnMobile = items.length > 2;

  return (
    <nav aria-label="Breadcrumb" className={className}>
      <ol className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 flex-wrap">
        {items.map((item, i) => {
          const isLast = i === items.length - 1;
          const isFirst = i === 0;
          const isHiddenOnMobile = shouldCollapseOnMobile && !isFirst && !isLast;

          return (
            <React.Fragment key={`${item.label}-${i}`}>
              {i > 0 && (
                <li
                  aria-hidden="true"
                  className={`text-gray-300 dark:text-gray-700 ${isHiddenOnMobile ? 'hidden sm:block' : ''}`}
                >
                  /
                </li>
              )}
              <li className={isHiddenOnMobile ? 'hidden sm:block' : ''}>
                {item.to && !isLast ? (
                  <Link
                    to={item.to}
                    className="hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <span
                    className={isLast ? 'text-gray-900 dark:text-white font-medium truncate max-w-[200px] inline-block align-middle' : ''}
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                )}
              </li>
              {shouldCollapseOnMobile && isFirst && (
                <>
                  <li aria-hidden="true" className="text-gray-300 dark:text-gray-700 sm:hidden">
                    /
                  </li>
                  <li aria-hidden="true" className="sm:hidden text-gray-400">…</li>
                </>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default PageBreadcrumb;
